import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useListWebhookEndpoints,
  useListWebhookDeliveries,
  useRedeliverWebhookDelivery,
  useUpdateWebhookEndpoint,
  getListWebhookDeliveriesQueryKey,
  getListWebhookEndpointsQueryKey,
} from "@workspace/api-client-react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, ArrowLeft, RefreshCw, Copy, AlertTriangle, Check, Code2 } from "lucide-react";

const AUTO_PAUSE_THRESHOLD = 20;

const BODY_PREVIEW_LIMIT = 2000;

function formatPayload(payload: Record<string, unknown> | null | undefined): string {
  if (!payload) return "";
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

async function copyToClipboard(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error("Could not copy to clipboard");
  }
}

function TruncatedBody({
  text,
  label,
  testidPrefix,
}: {
  text: string;
  label: string;
  testidPrefix: string;
}) {
  const isTruncated = text.length > BODY_PREVIEW_LIMIT;
  const preview = isTruncated ? `${text.slice(0, BODY_PREVIEW_LIMIT)}…` : text;
  return (
    <div className="space-y-1">
      <pre
        className="rounded-md bg-muted/40 p-2 text-xs overflow-x-auto whitespace-pre-wrap break-all"
        data-testid={`${testidPrefix}-preview`}
      >
        {preview}
      </pre>
      {isTruncated && (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            Showing first {BODY_PREVIEW_LIMIT.toLocaleString()} of{" "}
            {text.length.toLocaleString()} characters.
          </span>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => void copyToClipboard(text, label)}
            data-testid={`${testidPrefix}-copy-full`}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy full
          </Button>
        </div>
      )}
      {!isTruncated && (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => void copyToClipboard(text, label)}
            data-testid={`${testidPrefix}-copy`}
          >
            <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy
          </Button>
        </div>
      )}
    </div>
  );
}

function jsSnippet(secretPrefix: string): string {
  return `import express from "express";
import { verifyWebhook } from "@mailcraft/sdk";

const secret = "${secretPrefix}…";          // your full signing secret

app.post(
  "/webhooks/mailcraft",
  express.raw({ type: "application/json" }), // keep body as raw Buffer
  (req, res) => {
    // X-MailCraft-Signature / X-MailCraft-Timestamp (Node lowercases header keys)
    const signature = req.headers["x-mailcraft-signature"];
    const timestamp = req.headers["x-mailcraft-timestamp"];  // optional

    if (!verifyWebhook(req.body, signature, secret)) {
      return res.status(401).send("Invalid signature");
    }

    // signature is valid — process the event
    const event = JSON.parse(req.body.toString());
    console.log("Received", event.type);
    res.sendStatus(200);
  },
);`;
}

function pySnippet(secretPrefix: string): string {
  return `from mailcraft import verify_webhook

secret = "${secretPrefix}…"  # your full signing secret

@app.route("/webhooks/mailcraft", methods=["POST"])
def handle_webhook():
    signature = request.headers.get("X-MailCraft-Signature", "")
    timestamp = request.headers.get("X-MailCraft-Timestamp", "")  # optional

    if not verify_webhook(request.data, signature, secret):
        return "Invalid signature", 401

    # signature is valid — process the event
    event = request.get_json()
    print("Received", event["type"])
    return "", 200`;
}

function curlSnippet(secretPrefix: string): string {
  return `#!/usr/bin/env bash
SECRET="${secretPrefix}…"          # your full signing secret

# The raw request body and the signature header from the incoming request
BODY='{"type":"email.delivered","emailId":"abc123"}'
RECEIVED_SIG="$1"                  # X-MailCraft-Signature header value (sha256=<hex>)

# Strip the "sha256=" prefix and lowercase for safe comparison
RECEIVED_HEX=$(echo "$RECEIVED_SIG" | sed 's/^[Ss][Hh][Aa]256=//' | tr '[:upper:]' '[:lower:]')

EXPECTED_HEX=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SECRET" | sed 's/^.* //')

if [ "$RECEIVED_HEX" = "$EXPECTED_HEX" ]; then
  echo "✔ Signature valid"
else
  echo "✘ Signature mismatch"
  exit 1
fi`;
}

function VerificationSnippet({ secretPrefix }: { secretPrefix: string }) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null);

  function handleCopy(code: string, tab: string) {
    setCopiedTab(tab);
    void copyToClipboard(code, "Snippet");
    setTimeout(() => setCopiedTab(null), 2000);
  }

  const js = jsSnippet(secretPrefix);
  const py = pySnippet(secretPrefix);
  const sh = curlSnippet(secretPrefix);

  return (
    <Card data-testid="verification-snippet">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Code2 className="h-4 w-4" />
          Verify webhook signatures
        </CardTitle>
        <CardDescription className="text-xs">
          Copy this snippet into your server to verify that incoming webhooks are
          genuinely from MailCraft.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="node">
          <div className="flex items-center justify-between gap-2">
            <TabsList>
              <TabsTrigger value="node" data-testid="snippet-tab-node">Node.js</TabsTrigger>
              <TabsTrigger value="python" data-testid="snippet-tab-python">Python</TabsTrigger>
              <TabsTrigger value="curl" data-testid="snippet-tab-curl">cURL / Shell</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="node">
            <div className="relative">
              <pre className="rounded-md bg-muted/40 p-3 text-xs overflow-x-auto whitespace-pre font-mono" data-testid="snippet-code-node">
                {js}
              </pre>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => handleCopy(js, "node")}
                data-testid="snippet-copy-node"
              >
                {copiedTab === "node" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="python">
            <div className="relative">
              <pre className="rounded-md bg-muted/40 p-3 text-xs overflow-x-auto whitespace-pre font-mono" data-testid="snippet-code-python">
                {py}
              </pre>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => handleCopy(py, "python")}
                data-testid="snippet-copy-python"
              >
                {copiedTab === "python" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="curl">
            <div className="relative">
              <pre className="rounded-md bg-muted/40 p-3 text-xs overflow-x-auto whitespace-pre font-mono" data-testid="snippet-code-curl">
                {sh}
              </pre>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2"
                onClick={() => handleCopy(sh, "curl")}
                data-testid="snippet-copy-curl"
              >
                {copiedTab === "curl" ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default function WebhookDetailPage({ id }: { id: string }) {
  const endpointId = Number(id);
  const qc = useQueryClient();
  const listEndpoints = useListWebhookEndpoints();
  const endpoint = listEndpoints.data?.endpoints.find((e) => e.id === endpointId);

  const deliveriesQuery = useListWebhookDeliveries(endpointId, {
    query: {
      enabled: Number.isInteger(endpointId) && endpointId > 0,
      queryKey: getListWebhookDeliveriesQueryKey(endpointId),
    },
  });
  const deliveries = deliveriesQuery.data?.deliveries ?? [];

  const redeliverMutation = useRedeliverWebhookDelivery({
    mutation: {
      onSuccess: () => {
        toast.success("Redelivery enqueued");
        void qc.invalidateQueries({
          queryKey: getListWebhookDeliveriesQueryKey(endpointId),
        });
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });

  // PATCH active=true on the endpoint resets the failure counter and
  // clears pausedAt/pausedReason on the server, so we just need to
  // refetch the endpoint list afterwards for the banner to disappear.
  const resumeMutation = useUpdateWebhookEndpoint({
    mutation: {
      onSuccess: () => {
        toast.success("Endpoint re-enabled");
        void qc.invalidateQueries({
          queryKey: getListWebhookEndpointsQueryKey(),
        });
      },
      onError: (err: Error) => toast.error(err.message),
    },
  });
  const isPaused = !!endpoint?.pausedAt;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <Link href="/app/webhooks">
          <Button variant="ghost" size="sm" className="mb-2 -ml-2">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to webhooks
          </Button>
        </Link>
        <h1 className="text-2xl font-semibold">Webhook deliveries</h1>
        {endpoint && (
          <p className="text-sm text-muted-foreground font-mono break-all">
            {endpoint.url}
          </p>
        )}
        {endpoint && !isPaused && endpoint.consecutiveFailures > 0 && (
          <div
            className="flex items-center gap-2 mt-1 text-sm text-amber-600"
            data-testid="text-consecutive-failures"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {endpoint.consecutiveFailures} consecutive failure{endpoint.consecutiveFailures === 1 ? "" : "s"} — auto-pauses at {AUTO_PAUSE_THRESHOLD}
            </span>
          </div>
        )}
      </div>

      {endpoint?.secretPrefix && (
        <VerificationSnippet secretPrefix={endpoint.secretPrefix} />
      )}

      {isPaused && endpoint && (
        <Alert variant="destructive" data-testid="alert-endpoint-paused">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Endpoint auto-paused</AlertTitle>
          <AlertDescription className="space-y-3">
            <div>
              MailCraft stopped delivering events to this endpoint at{" "}
              {new Date(endpoint.pausedAt!).toLocaleString()} after{" "}
              {endpoint.consecutiveFailures} consecutive delivery failures.
              {endpoint.pausedReason && (
                <>
                  {" "}
                  Last reason:{" "}
                  <span className="font-mono text-xs">
                    {endpoint.pausedReason}
                  </span>
                  .
                </>
              )}
            </div>
            <div>
              <Button
                size="sm"
                variant="outline"
                disabled={resumeMutation.isPending}
                onClick={() =>
                  resumeMutation.mutate({
                    id: endpointId,
                    data: { active: true },
                  })
                }
                data-testid="button-resume-endpoint"
              >
                {resumeMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                )}
                Re-enable endpoint
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {deliveriesQuery.isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : deliveries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No deliveries yet for this endpoint.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deliveries.map((d) => (
            <Card key={d.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-sm font-mono flex items-center gap-2">
                      <StatusBadge status={d.status} />
                      {d.eventType}
                    </CardTitle>
                    <CardDescription className="text-xs font-mono mt-0.5 break-all">
                      event {d.eventId}
                    </CardDescription>
                  </div>
                  <div className="text-right shrink-0 text-xs text-muted-foreground">
                    <div>{new Date(d.createdAt).toLocaleString()}</div>
                    <div>
                      attempts: {d.attempts}
                      {d.lastStatusCode != null && ` · HTTP ${d.lastStatusCode}`}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {d.lastError && (
                  <div
                    className="text-xs text-destructive font-mono break-all"
                    data-testid={`text-delivery-error-${d.id}`}
                  >
                    {d.lastError}
                  </div>
                )}
                {d.payload && (
                  <details className="text-xs">
                    <summary
                      className="cursor-pointer text-muted-foreground select-none"
                      data-testid={`button-toggle-payload-${d.id}`}
                    >
                      Request payload
                    </summary>
                    <div className="mt-1">
                      <TruncatedBody
                        text={formatPayload(d.payload)}
                        label="Payload"
                        testidPrefix={`delivery-payload-${d.id}`}
                      />
                    </div>
                  </details>
                )}
                {d.lastResponseBody && (
                  <details className="text-xs">
                    <summary
                      className="cursor-pointer text-muted-foreground select-none"
                      data-testid={`button-toggle-response-${d.id}`}
                    >
                      Last response body
                    </summary>
                    <div className="mt-1">
                      <TruncatedBody
                        text={d.lastResponseBody}
                        label="Response body"
                        testidPrefix={`delivery-response-${d.id}`}
                      />
                    </div>
                  </details>
                )}
                {d.nextRetryAt && (
                  <div className="text-xs text-muted-foreground">
                    Next retry at {new Date(d.nextRetryAt).toLocaleString()}
                  </div>
                )}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={redeliverMutation.isPending}
                    onClick={() =>
                      redeliverMutation.mutate({ id: d.id })
                    }
                    data-testid={`button-redeliver-${d.id}`}
                  >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Redeliver
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "delivered")
    return <Badge variant="default" className="bg-green-600">delivered</Badge>;
  if (status === "failed")
    return <Badge variant="destructive">failed</Badge>;
  return <Badge variant="secondary">pending</Badge>;
}
